import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { statsApi, productsApi, shopifyApi } from '../lib/api';
import { safeStorage } from '../lib/safeStorage';
import { formatPrice, formatDate } from '../lib/utils';
import {
  Package,
  Layers,
  DollarSign,
  Plus,
  ArrowRight,
  TrendingUp,
  Zap,
  GitBranch,
  Download,
  CheckCircle,
  AlertCircle,
  Loader2,
  Trash2,
  ExternalLink,
  Code,
} from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [widgetStatus, setWidgetStatus] = useState(null);
  const [installingWidget, setInstallingWidget] = useState(false);
  const [uninstallingWidget, setUninstallingWidget] = useState(false);
  const [themeExtLink, setThemeExtLink] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, productsRes] = await Promise.all([
          statsApi.get(),
          productsApi.getAll(),
        ]);
        setStats(statsRes.data);
        setRecentProducts(productsRes.data.slice(0, 5));

        // Check widget status
        try {
          const wsRes = await shopifyApi.getWidgetStatus();
          setWidgetStatus(wsRes.data);
        } catch (e) {
          setWidgetStatus({ installed: false, reason: 'Could not check status' });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInstallWidget = async () => {
    setInstallingWidget(true);
    try {
      const res = await shopifyApi.installWidget();
      toast.success(res.data.message);
      setWidgetStatus({ installed: true, src: res.data.script_tag_url, method: res.data.method });
      if (res.data.app_embed_deep_link) {
        setThemeExtLink(res.data.app_embed_deep_link);
      }
    } catch (e) {
      const rawDetail = e.response?.data?.detail;
      const detail = typeof rawDetail === 'string' ? rawDetail : (Array.isArray(rawDetail) ? rawDetail.map(d => d.msg || d).join(', ') : 'Failed to install widget');
      const status = e.response?.status;
      if (status === 403 || detail.includes('re-install') || detail.includes('permissions') || detail.includes('Re-authorize') || detail.includes('expired')) {
        toast.error('Permissions update needed', {
          description: detail,
        });
        setWidgetStatus((prev) => ({ ...prev, needsReauth: true }));
      } else {
        toast.error(detail);
      }
    } finally {
      setInstallingWidget(false);
    }
  };

  const handleReauthorize = () => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const shopDomain = safeStorage.getItem('shop_domain') || '';
    if (shopDomain) {
      window.location.href = `${apiUrl}/api/shopify/auth?shop=${shopDomain}&force=true`;
    } else {
      toast.error('Shop domain not found. Go to Settings to connect your store.');
    }
  };

  const handleUninstallWidget = async () => {
    setUninstallingWidget(true);
    try {
      const res = await shopifyApi.uninstallWidget();
      toast.success(res.data.message);
      setWidgetStatus({ installed: false });
    } catch (e) {
      toast.error('Failed to uninstall widget');
    } finally {
      setUninstallingWidget(false);
    }
  };

  const statCards = [
    {
      title: 'Total Products',
      value: stats?.products_count || 0,
      icon: Package,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Option Sets',
      value: stats?.option_sets_count || 0,
      icon: Layers,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      title: 'Products with Options',
      value: stats?.products_with_options || 0,
      icon: Zap,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
    },
  ];

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Overview of your product options
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" data-testid="new-option-set-btn">
              <Link to="/option-sets/new">
                <Layers className="w-4 h-4 mr-2" />
                New Option Set
              </Link>
            </Button>
            <Button asChild data-testid="new-product-btn">
              <Link to="/products/new">
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Link>
            </Button>
          </div>
        </div>

        {/* Widget Installation Card */}
        <Card
          className="border-2 mb-6 border-accent/30 bg-accent/5"
          data-testid="widget-install-card"
        >
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="p-3 rounded-xl bg-accent/10">
                <Code className="w-8 h-8 text-accent" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold">Storefront Widget</h3>
                  <Badge className="bg-accent/10 text-accent border-accent/20 text-xs">
                    Theme Extension
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Activate the Product Options embed in your Theme Editor. The widget will automatically display custom options on your product pages.
                </p>
              </div>
              <Button
                size="lg"
                className="gap-2"
                data-testid="add-block-btn"
                onClick={() => {
                  const link = widgetStatus?.app_embed_deep_link || themeExtLink;
                  if (link) {
                    if (window.top !== window.self) {
                      window.top.location.href = link;
                    } else {
                      window.open(link, '_blank');
                    }
                  } else {
                    toast.info('Connect your Shopify store first.');
                  }
                }}
              >
                <ExternalLink className="w-5 h-5" />
                Activate in Theme Editor
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 stagger-children">
          {loading
            ? Array(3)
                .fill(0)
                .map((_, i) => (
                  <Card key={i} className="border-border">
                    <CardContent className="p-6">
                      <Skeleton className="h-4 w-24 mb-4" />
                      <Skeleton className="h-8 w-16" />
                    </CardContent>
                  </Card>
                ))
            : statCards.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card
                    key={stat.title}
                    className="border-border card-hover surface-gradient"
                    data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-muted-foreground">
                          {stat.title}
                        </span>
                        <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                          <Icon className={`w-5 h-5 ${stat.color}`} />
                        </div>
                      </div>
                      <p className="text-3xl font-bold mono">{stat.value}</p>
                    </CardContent>
                  </Card>
                );
              })}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 gap-6">
          {/* Recent Products */}
          <Card className="border-border" data-testid="recent-products">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Products</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/products">
                  View all <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {Array(3)
                    .fill(0)
                    .map((_, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <Skeleton className="w-12 h-12 rounded-lg" />
                        <div className="flex-1">
                          <Skeleton className="h-4 w-32 mb-2" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                    ))}
                </div>
              ) : recentProducts.length === 0 ? (
                <div className="empty-state py-8">
                  <Package className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No products yet</p>
                  <Button asChild size="sm">
                    <Link to="/products/new">Add your first product</Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentProducts.map((product) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.id}`}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-secondary transition-colors"
                    >
                      <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{product.title}</p>
                        <p className="text-sm text-muted-foreground mono">
                          {formatPrice(product.price)}
                        </p>
                      </div>
                      {product.option_set_id && (
                        <div className="p-1.5 rounded-full bg-accent/10">
                          <Zap className="w-4 h-4 text-accent" />
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-border mt-6" data-testid="quick-actions">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-start gap-2"
                asChild
              >
                <Link to="/option-sets/new">
                  <Layers className="w-5 h-5 text-accent" />
                  <div>
                    <p className="font-medium">Create Option Set</p>
                    <p className="text-xs text-muted-foreground">
                      Build custom product options
                    </p>
                  </div>
                </Link>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-start gap-2"
                asChild
              >
                <Link to="/pricing-rules">
                  <DollarSign className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="font-medium">Configure Pricing</p>
                    <p className="text-xs text-muted-foreground">
                      Set up price modifiers
                    </p>
                  </div>
                </Link>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-start gap-2"
                asChild
              >
                <Link to="/logic">
                  <GitBranch className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="font-medium">Add Logic Rules</p>
                    <p className="text-xs text-muted-foreground">
                      Create conditional options
                    </p>
                  </div>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
